/** @format */

import {QuickImagePreview} from '@/account/components/upload_designs/quick_image_preview';
import {Button, Select} from '@sttark/design-system';
import {IfElse, If} from 'frontier-utilities';
import React from 'react';

// styles
import './container.scss';
import {useReplaceCustomerDesignsHandler} from './use_replace_customer_designs_handler';
import Dropzone from 'react-dropzone';
import {useUploadHandler} from '@/utilities/with_upload_handler';

export default function UploadDesigns({designsOrders, onSave, orderId}) {
    const {
        unSavedDesigns,
        handleRemoveUnsavedDesigns,
        handleUpdateDesignId,
        savingReplacementDesigns,
        handleSaveReplacementDesigns,
        handleUploadComplete,
        handleCreateFileEntity,
        handleRemoveDesignId
    } = useReplaceCustomerDesignsHandler({
        orderId,
        onSave,
        designsOrders
    });

    const saveFileEndpoint = `/account/designs/get-matching-designs/${orderId}`;

    const {
        dropping,
        uploading,
        fileProgress,
        handleDragEnter,
        handleDragLeave,
        handleDrop,
        handleClickAddFiles,
        handleCancelUpload,
        handleProcessFile
    } = useUploadHandler({
        thumbnailField: 'thumb_key',
        saveFileEndpoint,
        onUploadComplete: handleUploadComplete,
        createFileEntity: handleCreateFileEntity
    });

    const designOptions = [
        {
            value: null,
            label: 'Choose a Design to Replace'
        },
        ...designsOrders
            .filter(({is_reorder}) => !is_reorder)
            .map(dos => {
                return {
                    value: dos.design_id,
                    label: dos.design_id + '_' + dos.design.customer_filename
                };
            })
    ];

    designOptions.sort((a, b) => {
        return a.id > b.id ? 1 : -1;
    });

    if (designOptions.length === 1) {
        return (
            <div className="tw-flex tw-items-center tw-justify-around" style={{height: '400px'}}>
                <div>
                    <div
                        style={{
                            width: '450px',
                            fontSize: '28px',
                            textAlign: 'center'
                        }}>
                        No Designs Can be Replaced
                        <hr />
                        <div style={{fontSize: '14px', lineHeight: '16px', textAlign: 'center'}}>
                            <em>
                                Reordered designs cannot be replaced.
                                <br />
                                <a href="/contact" className="color-accent-primary">
                                    <u>Contact Customer Care</u>
                                </a>{' '}
                                for next steps.
                            </em>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Dropzone
            style={{position: 'static'}}
            noClick={true}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            {({getRootProps}) => (
                <div {...getRootProps()}>
                    <div className="upload-designs-19kt">
                        <If
                            condition={
                                unSavedDesigns.length === 0 &&
                                Object.keys(fileProgress).length === 0
                            }>
                            <div
                                className="tw-flex tw-items-center tw-justify-around"
                                style={{height: '400px'}}>
                                <div className={' ' + dropping ? 'dropping' : ''}>
                                    <ol style={{marginBottom: '48px', paddingLeft: '24px'}}>
                                        <li
                                            style={{listStyle: 'numeric', fontSize: '24px'}}
                                            className="mb-4">
                                            Drop your files here or{' '}
                                            <button
                                                className="btn btn-link p-0 text-primary"
                                                onClick={handleClickAddFiles}>
                                                <u>Click Here</u>
                                            </button>{' '}
                                            to upload designs
                                        </li>
                                        <li style={{listStyle: 'numeric', fontSize: '24px'}}>
                                            Choose which designs to replace
                                        </li>
                                    </ol>
                                    <div
                                        style={{
                                            fontSize: '16px',
                                            lineHeight: '18px'
                                        }}>
                                        <em>
                                            Reordered designs cannot be replaced.{' '}
                                            <a href="/contact" className="color-accent-primary">
                                                <u>Contact Customer Care</u>
                                            </a>{' '}
                                            for next steps.
                                        </em>
                                    </div>
                                </div>
                            </div>
                        </If>
                        {/* This will display while a file is being processed from filepicker inpto our db */}
                        {Object.keys(fileProgress).map(filename => {
                            const progress = fileProgress[filename];
                            if (progress.totalPercent < 100 && !progress.error) {
                                return (
                                    <div
                                        className="file-progress__progress"
                                        key={`${filename}_uploading`}>
                                        <p>File: {filename}</p>
                                        <div>
                                            <div style={{width: `${progress.totalPercent}%`}} />
                                        </div>
                                        <span>{progress.totalPercent}%</span>
                                    </div>
                                );
                            }

                            if (progress.converting && !progress.error) {
                                return (
                                    <div
                                        className="file-progress__processing"
                                        key={`${filename}_uploading`}>
                                        <p>
                                            <b>File:</b> {filename}
                                        </p>
                                        <i className="fa fa-spinner fa-spin" />
                                        <span className="d-inline-block ms-3">Converting...</span>
                                    </div>
                                );
                            }

                            if (progress.error) {
                                return (
                                    <div
                                        className="file-progress__error"
                                        key={`${filename}_uploading`}>
                                        <p>
                                            <b>File:</b> {filename}
                                        </p>
                                        <div className="alert alert-danger" role="alert">
                                            {progress.error}
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={`${filename}_done`}
                                    className="pl-2 tw-pr-2 text-success"
                                    style={{fontSize: '16px'}}>
                                    <p>
                                        <b>File:</b> {filename}
                                    </p>
                                    <i className="fa fa-check" />
                                    <span className="d-inline-block ms-3">
                                        {progress.totalPercent}%. Please wait while we process your
                                        file...
                                    </span>
                                </div>
                            );
                        })}

                        {unSavedDesigns.map((design, index) => {
                            // Find the matching design order based on the selected design ID
                            const matchingDesignOrder =
                                design.id > 0
                                    ? designsOrders.find(({design_id}) => {
                                          return design_id === design.id;
                                      })
                                    : null;

                            let width = 0;
                            let height = 0;

                            if (matchingDesignOrder) {
                                width = matchingDesignOrder?.design.pressready_thumb_key
                                    ? designsOrders[0].design.pressready_thumb_width
                                    : designsOrders[0].design.customer_thumb_width;

                                height = matchingDesignOrder?.design.pressready_thumb_key
                                    ? designsOrders[0].design.pressready_thumb_height
                                    : designsOrders[0].design.customer_thumb_height;
                            }

                            return (
                                <div className="tw-mb-3 tw-pr-2" key={design.id + ' _' + index}>
                                    <div className="upload-designs-19kt_replace tw-mb-4">
                                        <div>
                                            <p className="p-0 m-0">
                                                <b>New</b>
                                            </p>
                                            <p className="mx-0 mb-4 color-alert-positive d-flex align-items-center justify-content-between gap-2">
                                                <span>{design.filename}</span>
                                                <button
                                                    onClick={() =>
                                                        handleRemoveUnsavedDesigns(design.id)
                                                    }
                                                    className="p-0 d-flex align-items-center justify-content-center gap-2">
                                                    <i className="fa fa-trash color-alert-negative" />
                                                    <u className="color-alert-negative">Remove</u>
                                                </button>
                                            </p>
                                            <p className="p-0 m-0">
                                                <b>Choose a Design to Replace</b>
                                            </p>
                                            <div className="mb-4">
                                                <Select
                                                    className="upload-designs-19kt__select"
                                                    options={designOptions.filter(({id}) => {
                                                        // Filter out designs already being replaced by other designs
                                                        return (
                                                            unSavedDesigns.findIndex(
                                                                ({id: unsavedDesignId}) =>
                                                                    unsavedDesignId === id
                                                            ) === -1 ||
                                                            id === design.id ||
                                                            id === -1
                                                        );
                                                    })}
                                                    label="Select Design"
                                                    onChange={(val, label) => {
                                                        handleUpdateDesignId(
                                                            design.filestack_id,
                                                            val
                                                        );
                                                    }}
                                                    value={design.id}
                                                    initialValue={
                                                        design.id > 0
                                                            ? designOptions.find(
                                                                  option =>
                                                                      option.value === design.id
                                                              )?.label
                                                            : null
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <If condition={matchingDesignOrder}>
                                            <div className="tw-mt-4">
                                                <QuickImagePreview
                                                    onRemove={() =>
                                                        handleRemoveDesignId(design.filestack_id)
                                                    }
                                                    thumbKey1={
                                                        matchingDesignOrder?.design
                                                            .pressready_thumb_key
                                                            ? matchingDesignOrder?.design
                                                                  .pressready_thumb_key
                                                            : matchingDesignOrder?.design
                                                                  .customer_thumb_key
                                                    }
                                                    thumbKey2={design.thumb_key}
                                                    filename={design.filename}
                                                    newFileName={
                                                        matchingDesignOrder?.design
                                                            .customer_filename
                                                    }
                                                    height={height}
                                                    width={width}
                                                />
                                            </div>
                                        </If>
                                    </div>
                                </div>
                            );
                        })}
                        <If condition={unSavedDesigns.length > 0}>
                            <p className="m-0 tw-p-0">
                                Designs will be replaced immediately when clicking "save designs."
                                Any approved proofs for this order will be automatically disapproved
                            </p>
                        </If>
                        <If
                            condition={unSavedDesigns.some(({id}) => {
                                return !(id > 0);
                            })}>
                            <div className="upload-designs-19kt__warning-alert">
                                Not all Designs have a Replacement Chosen
                            </div>
                        </If>

                        <If
                            condition={
                                unSavedDesigns.length > 0 &&
                                !unSavedDesigns.some(({id}) => {
                                    return !(id > 0);
                                })
                            }>
                            <div className="tw-flex tw-items-center tw-justify-end mt-4">
                                <IfElse condition={savingReplacementDesigns}>
                                    <button
                                        className="btn btn--primary tw-mt-4 w-100"
                                        disabled="disabled">
                                        Saving...
                                    </button>
                                    <button
                                        className="btn btn--primary mt-4 w-100"
                                        onClick={handleSaveReplacementDesigns}>
                                        Save Designs
                                    </button>
                                </IfElse>
                            </div>
                        </If>
                    </div>
                </div>
            )}
        </Dropzone>
    );
}
